import {
  CR_SUMMARY_ASSET_ELEMENT_TYPE,
  CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS,
  CREDIT_REPORT_ASSET_ID,
  IDENTIFICATION_OUTCOME,
  SEARCH_HISTORY_ITEM_TYPE,
} from '@/types/enums';
import { Address } from '@/types/profile';
import {
  CRFullGroupAccountDetails,
  FullCreditProfile,
} from '@/types/responseTypes';

export const MockCreditProfile: FullCreditProfile = {
  identificationOutcome: 'POSITIVE' as IDENTIFICATION_OUTCOME,
  account: {
    id: '2bc11742-2935-43ad-86c7-8dad487a5c06',
  },
  maxScore: 999,
  dashboard: {
    name: 'Mark',
    score: 550,
    scoreDelta: 0,
    assets: [
      {
        '@id': '88c8133c-9150-44ea-beaa-a5f3ca7428f3',
        id: 'ACTIONS' as CREDIT_REPORT_ASSET_ID,
        title: 'You have 3 things to work on',
        subtitle: 'Start doing these to help improve your score',
        elements: [
          {
            '@id': '54997f6d-d389-4a57-9c8d-26e9ad3bfa32',
            id: 'ACTION_UNUSED_CREDIT',
            text: 'Start demonstrating responsible credit use',
            link: {
              embeddedContent: {
                title: 'Start demonstrating responsible credit use',
                body: '<p>You’ve not been using your credit cards or overdraft much in recent months.</p> <p>Lenders like to see that you can use credit responsibly, and the longer you do so, the more positively this impacts your score. You can show this by only taking on credit that you can afford, so you can pay the balance off in full every month.</p> <p>If you’re not planning to use the card at all, closing the account will stop lenders questioning why you are not using the credit and also reduces the possible avenues for fraud.</p> <p>Consider finding out what rates and offers you’re approved for, and comparing them with your current cards.</p>\n',
                maximiserContent: {
                  theme: 'CREDIT_UTILISATION',
                  body:
                    '<p>Using credit regularly and responsibly improves your score.</p><p>Improve this factor by keeping the percentage of your total credit limit you are using between 1-25% and paying off the balance in full each month.</p><p>To maximise this score, keep the percentage of the credit limit used on each account below 25%</p>',
                  impact: 'HIGH',
                },
              },
              linkType: 'EMBEDDED',
            },
            type: 'TEXT',
          },
          {
            '@id': '41876dea-9111-4a93-b558-87436a0cfc21',
            id: 'ACTION_UNPAID_JUDGMENT',
            text: 'Pay off your judgment',
            link: {
              embeddedContent: {
                title: 'Pay off your judgment',
                body: '<p>Having a County Court, High Court Judgment or Decree on your credit file can make it difficult to get new credit.</p> <p>The sooner you pay off the judgment, the sooner your credit score can begin recovering (though it will continue to have an impact for six years from the issue date).</p> <p>Decrees won’t automatically be updated on your credit report. You’ll need to send proof to the court that the debt has been settled.</p>\n',
                maximiserContent: {
                  theme: 'JUDGEMENTS',
                  body:
                    '<p>County Court, High Court Judgment or Decrees can negatively impact your score</p><p>It can take for up to six years for them to no longer effect your credit score</p>',
                  impact: 'HIGH',
                },
              },
              linkType: 'EMBEDDED',
            },
            type: 'TEXT',
          },
          {
            '@id': 'd845794f-5fa8-4ab0-b827-0025ce714f73',
            id: 'ACTION_DEFAULTED_ACCOUNT',
            text: 'You’ve defaulted on an account',
            link: {
              embeddedContent: {
                title: 'You’ve defaulted on an account',
                body: '<p>One of your accounts is in default. This will stay on your credit file for six years from the date of default, during which it’ll be more difficult to get new credit.</p> <p>You can add a Notice of Correction to your report explaining the circumstances around the default if you think this may help with future applications. You can do this by raising a dispute.</p>\n',
                maximiserContent: {
                  theme: 'DEFAULTS',
                  body:
                    '<p>Defaulted accounts will stay on your credit file for six years from the date of default.</p><p>During this time, it will be more difficult to get new credit.</p>',
                  impact: 'HIGH',
                },
              },
              linkType: 'EMBEDDED',
            },
            type: 'TEXT',
          },
        ],
        type: 'COLLECTION',
      },
      {
        '@id': '8f0bf8c8-dd7a-4f09-b406-6edda9c3c4ac',
        id: 'RECENT_CHANGES' as CREDIT_REPORT_ASSET_ID,
        title: 'No score change this month',
        elements: [
          {
            '@id': '6607bf27-8558-4ec4-adf9-035554c92952',
            id: 'NO_SCORE_CHANGE',
            text: 'It looks like nothing changed this month',
            link: {
              embeddedContent: {
                title: 'It looks like nothing changed this month',
                body: 'Your score hasn’t changed this month.  Check in next month to see if your score is different and we’ll let you know the reasons why.',
              },
              linkType: 'EMBEDDED',
            },
            type: 'TEXT',
          },
        ],
        type: 'COLLECTION',
      },
      {
        '@id': '80e3d620-0fec-4ad6-ac6f-adfc80e01727',
        id: 'POSITIVE_REINFORCEMENTS' as CREDIT_REPORT_ASSET_ID,
        title: "You're doing 4 things well",
        subtitle: 'Keep doing these to help improve your score',
        elements: [
          {
            '@id': 'e5030e78-4a70-45ff-89fe-9462596f6945',
            id: 'POSITIVE_REINFORCEMENT_REGISTERED_TO_VOTE',
            text: 'You’re registered to vote',
            link: {
              embeddedContent: {
                title: 'You’re registered to vote',
                body: '<p>Being registered to vote makes it easier for lenders to find you when they check your identity. This gives your score a healthy boost.</p> <p>This will also improve your score over time. Generally speaking, staying at the same address is viewed positively by lenders as they take it as a sign of stability.</p>\n',
                maximiserContent: {
                  theme: 'ELECTORAL_ROLL',
                  body:
                    "<p>Register to vote at your current address to get an initial boost to your score.</p><p>If you've recently updated your electoral roll information – it can take up to 10 weeks for this to show in your report.</p><p>After a year and a half on the electoral roll, you will see improvement to your score.</p><p>After six years at the same address, you wll have maximised the impact on your score.</p>",
                  impact: 'MEDIUM',
                },
              },
              linkType: 'EMBEDDED',
            },
            type: 'TEXT',
          },
          {
            '@id': 'fdee1bc1-91b7-4e1b-a2c5-a225121220d0',
            id: 'POSITIVE_REINFORCEMENT_ALL_PAYMENTS_ON_TIME',
            text: 'You’re paying your accounts on time',
            link: {
              embeddedContent: {
                title: 'You’re paying your accounts on time',
                body: '<p>Paying all your accounts on time not only prevents costly late fees, but also improves your credit score.</p> <p>Lenders view responsible use of credit positively, so keep meeting at least the minimum payment (and preferably more) every month, and over time this will have a positive effect on your score.</p>\n',
                maximiserContent: {
                  theme: 'PAYMENT_HISTORY',
                  body:
                    "<p>Lenders want to see a history of payments made on time.</p><p>For each missed payment, it takes 16 months for your score to recover</p><p>If you're unable to make a payment, contact your lender to see if you can make an arrangement before the missed payment is reported to the credit reference agencies.</p>",
                  impact: 'HIGH',
                },
              },
              linkType: 'EMBEDDED',
            },
            type: 'TEXT',
          },
          {
            '@id': 'ee2ad17b-95e4-42ef-8eaf-39cdab51aa73',
            id: 'POSITIVE_REINFORCEMENT_LONG_STANDING_ACCOUNT',
            text: 'You’ve got a long-standing account',
            link: {
              embeddedContent: {
                title: 'You’ve got a long-standing account',
                body: '<p>You have an account that is at least three years old.</p> <p>Generally, having an account for a long time is viewed positively by lenders as they take it as a sign of stability. As long as you use this responsibly, you’ll continue to demonstrate a good track record and your score will improve.</p> <p>If you’re not using the account at all closing the account may temporarily lower your score, but it is still useful as it will stop lenders questioning why you are not using the credit and also reduces the possible avenues for fraud.</p>\n',
                maximiserContent: {
                  theme: 'AGE_OF_ACCOUNT',
                  body:
                    '<p>Having an account open for more than a year will help improve this factor</p><p>After 6 years, points will be added to your score.</p><p>The longer the age of your active account, the better your score.</p>',
                  impact: 'MEDIUM',
                },
              },
              linkType: 'EMBEDDED',
            },
            type: 'TEXT',
          },
          {
            '@id': '9fa60542-250b-4524-9cc4-c267dd2a9d39',
            id: 'POSITIVE_REINFORCEMENT_SAME_ADDRESS_LONG_TIME',
            text: 'You’ve been at your address for a long time',
            link: {
              embeddedContent: {
                title: 'You’ve been at your address for a long time',
                body: '<p>You’ve been registered to vote at the same address for a long time. This will improve your score over time.</p> <p>This is because, generally speaking, staying at the same address is viewed positively by lenders as they take it as a sign of stability.</p>\n',
                maximiserContent: {
                  theme: 'ELECTORAL_ROLL',
                  body:
                    "<p>Register to vote at your current address to get an initial boost to your score.</p><p>If you've recently updated your electoral roll information – it can take up to 10 weeks for this to show in your report.</p><p>After a year and a half on the electoral roll, you will see improvement to your score.</p><p>After six years at the same address, you wll have maximised the impact on your score.</p>",
                  impact: 'MEDIUM',
                },
              },
              linkType: 'EMBEDDED',
            },
            type: 'TEXT',
          },
        ],
        type: 'COLLECTION',
      },
    ],
    creditBand: {
      value: 'VERY_LOW',
      tagline: "You've got room to grow",
      message: 'Let’s see how you can improve your score',
    },
  },
  report: {
    metadata: {
      referenceAgencyUserId: '961478d5-2a6d-466c-acf1-48d9dbe274f4',
      outcome: 'POSITIVE',
      dateOfReport: '2023-09-27T14:21:45Z',
      providerSupportReference: '90247d5f-f687-47ca-8661-a3cd4799fef1',
    },
    summary: {
      assets: [
        {
          '@id': '572a3302-713a-4213-9da5-31c41970640c',
          id: 'CREDIT_CARDS' as CREDIT_REPORT_ASSET_ID,
          title: 'Credit cards',
          elements: [
            {
              '@id': '7a7dfbe0-1244-47a2-a7c7-1420f40dbea2',
              id: 'CREDIT_CARDS_TOTAL',
              start: 0,
              value: 500,
              end: 1000,
              type: 'ACCOUNT_TOTAL' as CR_SUMMARY_ASSET_ELEMENT_TYPE,
            },
            {
              '@id': '285e4d81-264f-42ce-b712-8c735c6df135',
              accountLinkId: '8d987a7e-00aa-442e-8f81-b77f9475436f',
              id: 'CREDIT_CARDS_ACCOUNT_393',
              title: 'Abc Bank credit card',
              start: 0,
              value: 500,
              end: 1000,
              updatedDate: '2020-07-07T23:00:00Z',
              type: 'ACCOUNT_PROGRESS' as CR_SUMMARY_ASSET_ELEMENT_TYPE,
            },
          ],
          type: 'COLLECTION',
        },
        {
          '@id': '2d5a20b7-0446-4f0c-8422-e0003547494f',
          id: 'LOANS' as CREDIT_REPORT_ASSET_ID,
          title: 'Loans',
          elements: [
            {
              '@id': '4f054eae-92e8-4fcc-9a13-9ff96b6b398f',
              id: 'LOANS_TOTAL',
              start: 100,
              value: 500,
              end: 0,
              regularRepaymentAmount: 0,
              type: 'ACCOUNT_TOTAL' as CR_SUMMARY_ASSET_ELEMENT_TYPE,
            },
            {
              '@id': '5768ebb3-3fd3-49e6-b97a-e62271f65c8f',
              accountLinkId: '20e0df32-6a06-450e-a7bf-0153dd999899',
              id: 'LOANS_ACCOUNT_1575',
              title: 'Test Lender loan (unspecified type)',
              start: 100,
              value: 500,
              end: 0,
              updatedDate: '2020-07-07T23:00:00Z',
              type: 'ACCOUNT_PROGRESS' as CR_SUMMARY_ASSET_ELEMENT_TYPE,
            },
          ],
          type: 'COLLECTION',
        },
        {
          '@id': '61ba4415-63d6-4b6d-b8c4-3269c0ecc900',
          id: 'MORTGAGES' as CREDIT_REPORT_ASSET_ID,
          title: 'Mortgages',
          elements: [
            {
              '@id': '16fec8a1-8d7e-457c-aa2c-5b58366a6444',
              id: 'MORTGAGES_TOTAL',
              start: 200,
              value: 135000,
              end: 0,
              regularRepaymentAmount: 0,
              type: 'ACCOUNT_TOTAL' as CR_SUMMARY_ASSET_ELEMENT_TYPE,
            },
            {
              '@id': 'f40fe8ec-386a-48fa-8755-36c697f8f0a4',
              accountLinkId: '1826370a-9608-44e7-b246-11efdfc9cde2',
              id: 'MORTGAGES_ACCOUNT_1230',
              title: 'Test Lender mortgage (unspecified type)',
              start: 100,
              value: 50000,
              end: 0,
              updatedDate: '2020-07-07T23:00:00Z',
              type: 'ACCOUNT_PROGRESS' as CR_SUMMARY_ASSET_ELEMENT_TYPE,
            },
            {
              '@id': '915a5502-8a16-473a-9267-fea149d03b54',
              accountLinkId: '538c35ce-07ee-4e16-b17b-636633b0cdc8',
              id: 'MORTGAGES_ACCOUNT_1231',
              title: 'Test Lender residential mortgage',
              start: 100,
              value: 85000,
              end: 0,
              updatedDate: '2020-07-07T23:00:00Z',
              type: 'ACCOUNT_PROGRESS' as CR_SUMMARY_ASSET_ELEMENT_TYPE,
            },
          ],
          type: 'COLLECTION',
        },
      ],
    },
    accounts: {
      groups: [
        {
          id: CREDIT_REPORT_ASSET_ID.CREDIT_CARDS,
          name: 'Credit cards',
          accounts: [
            {
              accountLinkId: '26369618-9b50-45f6-b810-4a3615572ef0',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.CREDIT_CARD,
                code: 'CC',
                description: 'Credit Card',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Credit%20cards.pdf',
              },
              history: [
                {
                  date: '2023-10-03',
                  value: {
                    limit: 10,
                    balance: 11,
                    paymentAmount: 12,
                    statementBalance: 13,
                    numberOfCashAdvances: 14,
                    valueOfCashAdvances: 15,
                    paymentStatus:
                      'The lender hasn’t been able to give the account status for this month',
                    paymentStatusDescription: 'paymentStatusDescription',
                    accountStatus: 'Unclassified or recently opened account payment delay',
                    accountDescription: 'O2 Telecommunications',
                  },
                },
                {
                  date: '2022-10-03',
                  value: {
                    limit: 10,
                    balance: 11,
                    paymentAmount: 12,
                    statementBalance: 13,
                    numberOfCashAdvances: 14,
                    valueOfCashAdvances: 15,
                    paymentStatus:
                      'The lender 2222hasn’t been able to give the account status for this month',
                    paymentStatusDescription: 'paymentStatusDescription',
                    accountStatus: 'Unclassified 2222or recently opened account payment delay',
                    accountDescription: 'O2 Tel22222ecommunications',
                  },
                },
              ],
              lenderName: 'Lender Fai Acc Match',
              accountNumber: '**************-781',
              accountId: 710,
              memberPortId: 288,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                accountHolderId: 706,
              },
              balance: 0,
              currencyCode: 'GBP',
              updatedDate: '2019-07-08T23:00:00Z',
              accountStartDate: '2019-07-08T23:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              paymentStartDate: '2007-10-18T23:00:00Z',
              repaymentFrequency: 'Monthly',
              status: 'UP_TO_DATE',
              additionalStatus: {
                value: 'noAction',
                shortDescription: 'Your payments are up-to-date',
                description: 'Your payments are up-to-date',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'GOOD',
            },
            {
              accountLinkId: '8b49df76-1aac-4aac-a5f1-6b94155ab0f6',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.CREDIT_CARD,
                code: 'CC',
                description: 'Credit Card',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Credit%20cards.pdf',
              },
              lenderName: 'Test Lender Duplicate',
              accountNumber: '**************-780 1',
              accountId: 780,
              memberPortId: 288,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                accountHolderId: 776,
              },
              balance: 0,
              currencyCode: 'GBP',
              updatedDate: '2019-07-08T23:00:00Z',
              accountStartDate: '2019-07-08T23:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              paymentStartDate: '2007-10-18T23:00:00Z',
              repaymentFrequency: 'Monthly',
              status: 'UP_TO_DATE',
              additionalStatus: {
                value: 'noAction',
                shortDescription: 'Your payments are up-to-date',
                description: 'Your payments are up-to-date',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'GOOD',
            },
            {
              accountLinkId: 'a3ad63e6-8de9-4ac1-8ae4-24e4b826ebc5',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.CREDIT_CARD,
                code: 'CC',
                description: 'Credit Card',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Credit%20cards.pdf',
              },
              lenderName: 'Test Lender Duplicate',
              accountNumber: '**************-780 1',
              accountId: 781,
              memberPortId: 288,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                accountHolderId: 776,
              },
              balance: 0,
              currencyCode: 'GBP',
              updatedDate: '2019-07-08T23:00:00Z',
              accountStartDate: '2019-07-08T23:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              paymentStartDate: '2007-10-18T23:00:00Z',
              repaymentFrequency: 'Monthly',
              status: 'UP_TO_DATE',
              additionalStatus: {
                value: 'noAction',
                shortDescription: 'Your payments are up-to-date',
                description: 'Your payments are up-to-date',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'GOOD',
            },
            {
              accountLinkId: '79ca87c7-49eb-437e-8515-f94c1bbd48c9',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.CREDIT_CARD,
                code: 'CC',
                description: 'Credit Card',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Credit%20cards.pdf',
              },
              lenderName: 'Test Lender Duplicate',
              accountNumber: '**************-780 1',
              accountId: 783,
              memberPortId: 288,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                accountHolderId: 776,
              },
              balance: 0,
              currencyCode: 'GBP',
              updatedDate: '2019-07-08T23:00:00Z',
              accountStartDate: '2019-07-08T23:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              paymentStartDate: '2007-10-18T23:00:00Z',
              repaymentFrequency: 'Monthly',
              status: 'UP_TO_DATE',
              additionalStatus: {
                value: 'noAction',
                shortDescription: 'Your payments are up-to-date',
                description: 'Your payments are up-to-date',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'GOOD',
            },
          ],
        },
        {
          id: CREDIT_REPORT_ASSET_ID.CURRENT_ACCOUNTS,
          name: 'Current accounts',
        },
        {
          id: CREDIT_REPORT_ASSET_ID.LOANS,
          name: 'Loans',
          accounts: [
            {
              accountLinkId: 'c3f8dc7a-7ba0-4249-bb57-30fd965554ba',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.LOAN,
                code: 'LN',
                description: 'Loan (unspecified type)',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Loans.pdf',
              },
              lenderName: 'Test Lender 2 Duplicate',
              accountNumber: '**************3557 1',
              accountId: 2147483557,
              memberPortId: 286,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                startDate: '2019-01-18T00:00:00Z',
                accountHolderId: 2147483557,
              },
              balance: 500,
              currencyCode: 'GBP',
              updatedDate: '2019-05-20T23:00:00Z',
              accountStartDate: '2019-03-09T00:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              paymentStartDate: '2006-01-26T00:00:00Z',
              repaymentFrequency: 'Monthly',
              status: 'UP_TO_DATE',
              additionalStatus: {
                value: 'noAction',
                shortDescription: 'Your payments are up-to-date',
                description: 'Your payments are up-to-date',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'GOOD',
            },
            {
              accountLinkId: '6aae3fd3-2b03-499e-8265-276c93d45f71',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.LOAN,
                code: 'LN',
                description: 'Loan (unspecified type)',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Loans.pdf',
              },
              lenderName: 'Test Lender 2 Duplicate',
              accountNumber: '**************3557 1',
              accountId: 2147483558,
              memberPortId: 286,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                startDate: '2019-01-18T00:00:00Z',
                accountHolderId: 2147483558,
              },
              balance: 500,
              currencyCode: 'GBP',
              updatedDate: '2019-05-20T23:00:00Z',
              accountStartDate: '2019-03-09T00:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              paymentStartDate: '2006-01-26T00:00:00Z',
              repaymentFrequency: 'Monthly',
              status: 'UP_TO_DATE',
              additionalStatus: {
                value: 'noAction',
                shortDescription: 'Your payments are up-to-date',
                description: 'Your payments are up-to-date',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'GOOD',
            },
          ],
        },
        {
          id: CREDIT_REPORT_ASSET_ID.MORTGAGES,
          name: 'Mortgages',
        },
        {
          id: CREDIT_REPORT_ASSET_ID.TELECOMMUNICATIONS,
          name: 'Telecommunications',
          accounts: [
            {
              accountLinkId: '793a52ab-d6bc-4702-82b5-a05166d851b9',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.TELECOMMUNICATION,
                code: 'TM',
                description: 'Telecommunications Supplier',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Telecoms.pdf',
              },
              lenderName: 'Test Lender 3 Name',
              accountNumber: '**************3608 1',
              accountId: 2147483608,
              memberPortId: 289,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                startDate: '2017-07-17T23:00:00Z',
                accountHolderId: 2147483608,
              },
              balance: 500,
              currencyCode: 'GBP',
              updatedDate: '2019-07-08T23:00:00Z',
              accountStartDate: '2018-06-08T23:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              repaymentFrequency: 'Monthly',
              status: 'DEFAULT',
              additionalStatus: {
                value: 'actionRequired',
                shortDescription: 'Your account is in default',
                description: 'Your account is in default',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'BAD',
              defaultDate: '2018-06-08T23:00:00Z',
              defaultBalance: 100,
            },
            {
              accountLinkId: 'e7d5d22b-08fb-49e7-a29e-eaeb6b4d86e2',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.TELECOMMUNICATION,
                code: 'TM',
                description: 'Telecommunications Supplier',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Telecoms.pdf',
              },
              lenderName: 'Test Lender 34 Name',
              accountNumber: '**************3608 1',
              accountId: 2147483609,
              memberPortId: 289,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                startDate: '2017-07-17T23:00:00Z',
                accountHolderId: 2147483609,
              },
              balance: 500,
              currencyCode: 'GBP',
              updatedDate: '2019-07-08T23:00:00Z',
              accountStartDate: '2018-06-08T23:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              repaymentFrequency: 'Monthly',
              status: 'DEFAULT',
              additionalStatus: {
                value: 'actionRequired',
                shortDescription: 'Your account is in default',
                description: 'Your account is in default',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'BAD',
              defaultDate: '2018-06-08T23:00:00Z',
              defaultBalance: 100,
            },
          ],
        },
        {
          id: CREDIT_REPORT_ASSET_ID.UTILITIES,
          name: 'Utilities',
          accounts: [
            {
              accountLinkId: 'd58cdcda-0698-4dc4-a531-1e8804754d6f',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.UTILITY,
                code: 'DU',
                description: 'Dual Fuel',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Utilities.pdf',
              },
              lenderName: 'Test Lender 4 Start Date',
              accountNumber: '**************3557 1',
              accountId: 2147483158,
              memberPortId: 286,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                startDate: '2019-01-20T00:00:00Z',
                accountHolderId: 2147483158,
              },
              balance: 500,
              currencyCode: 'GBP',
              updatedDate: '2019-05-20T23:00:00Z',
              accountStartDate: '2019-03-20T00:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              paymentStartDate: '2006-01-26T00:00:00Z',
              repaymentFrequency: 'Monthly',
              status: 'UP_TO_DATE',
              additionalStatus: {
                value: 'noAction',
                shortDescription: 'Your payments are up-to-date',
                description: 'Your payments are up-to-date',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'GOOD',
            },
            {
              accountLinkId: '7df0e6b7-64d3-4f1a-b157-3c50a2292bf3',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.UTILITY,
                code: 'DU',
                description: 'Dual Fuel',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Utilities.pdf',
              },
              lenderName: 'Test Lender 4 Start Date',
              accountNumber: '**************3557 1',
              accountId: 2147483258,
              memberPortId: 286,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                startDate: '2019-01-18T00:00:00Z',
                accountHolderId: 2147483258,
              },
              balance: 500,
              currencyCode: 'GBP',
              updatedDate: '2019-05-20T23:00:00Z',
              accountStartDate: '2019-03-09T00:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              paymentStartDate: '2006-01-26T00:00:00Z',
              repaymentFrequency: 'Monthly',
              status: 'UP_TO_DATE',
              additionalStatus: {
                value: 'noAction',
                shortDescription: 'Your payments are up-to-date',
                description: 'Your payments are up-to-date',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'GOOD',
            },
            {
              accountLinkId: 'a759c7b2-32dc-4a9a-aeaf-b94977f97ce1',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.UTILITY,
                code: 'EE',
                description: 'Electricity',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Utilities.pdf',
              },
              lenderName: 'Test Lender 5 Acc Number',
              accountNumber: '**************3557 1',
              accountId: 1147483558,
              memberPortId: 286,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                startDate: '2019-01-18T00:00:00Z',
                accountHolderId: 1147483558,
              },
              balance: 500,
              currencyCode: 'GBP',
              updatedDate: '2019-05-20T23:00:00Z',
              accountStartDate: '2019-03-09T00:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              paymentStartDate: '2006-01-26T00:00:00Z',
              repaymentFrequency: 'Monthly',
              status: 'UP_TO_DATE',
              additionalStatus: {
                value: 'noAction',
                shortDescription: 'Your payments are up-to-date',
                description: 'Your payments are up-to-date',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'GOOD',
            },
            {
              accountLinkId: '09125c09-8f48-4ddb-a1fc-a53347ef471f',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.UTILITY,
                code: 'EE',
                description: 'Electricity',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Utilities.pdf',
              },
              lenderName: 'Test Lender 5 Acc Number',
              accountNumber: '**************3558 1',
              accountId: 1137483558,
              memberPortId: 286,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                startDate: '2019-01-18T00:00:00Z',
                accountHolderId: 1137483558,
              },
              balance: 500,
              currencyCode: 'GBP',
              updatedDate: '2019-05-20T23:00:00Z',
              accountStartDate: '2019-03-09T00:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              paymentStartDate: '2006-01-26T00:00:00Z',
              repaymentFrequency: 'Monthly',
              status: 'UP_TO_DATE',
              additionalStatus: {
                value: 'noAction',
                shortDescription: 'Your payments are up-to-date',
                description: 'Your payments are up-to-date',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'GOOD',
            },
          ],
        },
        {
          id: CREDIT_REPORT_ASSET_ID.OTHER,
          name: 'Other',
          accounts: [
            {
              accountLinkId: 'b0f1d664-acd1-4181-a9b9-96bb6fef0d00',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.OTHER,
                code: 'AD',
                description: 'Asset Discounting',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Other.pdf',
              },
              lenderName: 'Test Lender 6 Acc Type',
              accountNumber: '**************3558 1',
              accountId: 1137483118,
              memberPortId: 286,
              open: false,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                startDate: '2019-01-18T00:00:00Z',
                accountHolderId: 1137483118,
              },
              balance: 500,
              currencyCode: 'GBP',
              updatedDate: '2019-05-20T23:00:00Z',
              accountStartDate: '2019-03-09T00:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              paymentStartDate: '2006-01-26T00:00:00Z',
              repaymentFrequency: 'Monthly',
              status: 'UP_TO_DATE',
              additionalStatus: {
                value: 'noAction',
                shortDescription: 'Your payments are up-to-date',
                description: 'Your payments are up-to-date',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'GOOD',
            },
            {
              accountLinkId: '4df75e4f-da62-40d9-be0b-e0cfc091b7fa',
              type: {
                classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.OTHER,
                code: 'BD',
                description: 'Budget Account',
              },
              logoUrl: {
                pdf: 'https://www.moneysupermarket.com/content/dam/moneysupermarket/images/content/creditmonitor/Other.pdf',
              },
              lenderName: 'Test Lender 6 Acc Type',
              accountNumber: '**************3558 1',
              accountId: 1137483119,
              memberPortId: 286,
              open: true,
              accountHolderDetails: {
                name: 'Mr Mark  Kirk',
                address: '1 Monopoly Close Test Town X9 5HJ',
                dateOfBirth: '1990-12-26',
                startDate: '2019-01-18T00:00:00Z',
                accountHolderId: 1137483119,
              },
              balance: 500,
              currencyCode: 'GBP',
              updatedDate: '2019-05-20T23:00:00Z',
              accountStartDate: '2019-03-09T00:00:00Z',
              promotionalRate: false,
              openingBalance: 100,
              paymentStartDate: '2006-01-26T00:00:00Z',
              repaymentFrequency: 'Monthly',
              status: 'UP_TO_DATE',
              additionalStatus: {
                value: 'noAction',
                shortDescription: 'Your payments are up-to-date',
                description: 'Your payments are up-to-date',
              },
              minimumPayment: false,
              statusSubjectiveLevel: 'GOOD',
            },
          ],
        },
      ],
    },
    personal: {
      personalInformation: {
        name: 'Mr Mark Kirk',
        currentAddress: '1 Monopoly Close Test Town X9 5HJ',
      },
      addressLinks: {
        addresses: [
          '1 , Monopoly Close, Test Town, X9 5HJ',
          '1 , Monopoly Close, Test Town, X9 5HJ',
        ],
        details: [
          {
            addressFrom: '1 , Monopoly Close, Test Town, X9 5HJ',
            addressTo: '1 , Monopoly Close, Test Town, X9 5HJ',
            mostRecentSource: 'Callcredit',
            earliestConfirmationOn: '2004-01-01T00:00:00Z',
            lastConfirmationOn: '2004-01-01T00:00:00Z',
            addressLinkId: 57726502,
          },
        ],
      },
      electoralRoll: {
        electoralRollId: 289306406,
        currentStatus: 'PRESENT',
        name: 'Mark  Kirk',
        address: '1 Monopoly Close Test Town X9 5HJ',
        startDate: '2004-06-13T23:00:00Z',
      },
    },
    connections: {
      financialConnections: [
        {
          name: 'Mr Mark  Kirk',
          source: 'Callcredit',
          lastConfirmedOn: '2004-01-01T00:00:00Z',
          confirmedOn: '2004-01-01T00:00:00Z',
          associateId: 22878398,
          notices: [],
        },
        {
          name: 'Mr Mark Kirk',
          source: 'Callcredit',
          lastConfirmedOn: '2004-01-01T00:00:00Z',
          confirmedOn: '2004-01-01T00:00:00Z',
          associateId: 22878400,
          notices: [],
        },
      ],
    },
    searchHistory: {
      searches: [
        {
          applicationType: 'SOLE',
          searchType: SEARCH_HISTORY_ITEM_TYPE.SOFT,
          searchPurpose: 'Identity Check',
          searchDate: '2023-06-09T12:06:09Z',
          searchReference: '{9EB9BAF6-D01F-4A5E-A57A-3674BD30D3E6}',
          company: 'Companyaname Test',
          name: 'Mr Mark Kirk',
          address: '1 Monopoly Close Test Town X9 5HJ',
          dateOfBirth: '1990-12-26',
        },
      ],
    },
    publicRecords: {
      judgments: [
        {
          address: '1 Monopoly Close Test Town X9 5HJ',
          amount: 1030,
          caseNumber: 'TEST 7825225',
          casePerId: 8145916,
          courtName: 'Testtown',
          courtType: 'County Court Judgment',
          currencyCode: 'GBP',
          judgmentDate: '2019-06-23T23:00:00Z',
          name: 'Mark  Kirk',
          status: 'Active',
          additionalStatus: {
            value: 'information',
            shortDescription: 'Active',
            description: 'Active',
          },
        },
        {
          address: '1 Monopoly Close Test Town X9 5HJ',
          amount: 8000,
          caseNumber: 'TEST 7825226',
          casePerId: 8145917,
          courtName: 'Testtown',
          courtType: 'County Court Judgment',
          currencyCode: 'GBP',
          judgmentDate: '2017-02-25T00:00:00Z',
          name: 'Mark  Kirk',
          status: 'Active',
          additionalStatus: {
            value: 'information',
            shortDescription: 'Active',
            description: 'Active',
          },
        },
        {
          address: '1 Monopoly Close Test Town X9 5HJ',
          amount: 2500,
          caseNumber: 'TEST 7825224',
          casePerId: 8145915,
          courtName: 'Testtown',
          courtType: 'County Court Judgment',
          currencyCode: 'GBP',
          judgmentDate: '2016-04-23T23:00:00Z',
          name: 'Mark  Kirk',
          satisfiedDate: '2018-04-08T23:00:00Z',
          status: 'Satisfied',
          additionalStatus: {
            value: 'information',
            shortDescription: 'Satisfied',
            description: 'Satisfied',
          },
        },
        {
          address: '1 Monopoly Close Test Town X9 5HJ',
          amount: 2700,
          caseNumber: 'TEST 7825223',
          casePerId: 8145914,
          courtName: 'Testtown',
          courtType: 'County Court Judgment',
          currencyCode: 'GBP',
          judgmentDate: '2015-11-30T00:00:00Z',
          name: 'Mark  Kirk',
          satisfiedDate: '2018-02-05T00:00:00Z',
          status: 'Satisfied',
          additionalStatus: {
            value: 'information',
            shortDescription: 'Satisfied',
            description: 'Satisfied',
          },
        },
      ],
    },
  },
  tooltips: {
    'public-records-bankruptcies-and-insolvencies': {
      title: 'Bankruptcies & insolvencies',
      body: '<p>These are any insolvencies or bankruptcies registered in your name. These records will stay on your file for six years from the issue date.</p> <p>Bankruptcy Restriction Undertakings or Orders can extend this period, as can Individual Voluntary Arrangements where you have to meet criteria for it to be completed.</p> <p>These carry restrictions, which will limit or block your ability to get credit while in effect.</p>\n',
    },
    'personal-personal-information': {
      title: 'Personal information',
      body: '<p>These are the details you initially gave us to find your credit file.</p> <p>If you’ve changed your name or moved house, please contact us to get them updated.</p> <p>Linked addresses are any addresses associated with you, as provided by lenders you have accounts with. In most cases these will be previous addresses you’ve moved from/to.</p> <p>Check for any mistakes – if you spot anything, contact the ‘source’ lender and get it corrected. This will make your credit report more consistent.</p>\n',
    },
    'personal-other-names': {
      title: 'Other names',
      body: '<p>These are any other names you’ve been known by under any accounts (open and closed) that has been included on your report.</p> <p>This might include your maiden name or any variation of your full name given to the lender in question.</p> <p>Check for spelling mistakes – if you spot anything, contact the ‘source’ lender and get it corrected. This will make your credit report more consistent.</p>\n',
    },
    'public-records-cifas-flags': {
      title: 'Cifas flags',
      body: '<p>The <a href="https://www.cifas.org.uk" target="_blank" rel="noopener">Credit Industry Fraud Avoidance System</a>, or Cifas, is the national fraud prevention service.</p> <p>If you’ve taken out Protective Registration with Cifas or one of the providers you have an account with has flagged you as a victim of fraud, it will show here.</p> <p>A Cifas flag doesn’t affect your credit score. It means you’ve been recognised as being at a higher risk of identity theft, and so companies using the service will take extra care with applications in your name.</p>\n',
    },
    'summary-credit-cards': {
      title: 'Credit cards',
      body: '<p>These are the balances and limits of any credit cards you have. A negative balance means you’ve overpaid on the card.</p> <p>These balances are not real time - they’re updated every four to six weeks by the bank or lender, so can be up to ten weeks behind depending on when your report was last updated.</p> <p>The balances and limits are used to calculate the percentage of credit you’re using, out of what’s available to you. This is known as your ‘credit utilisation’.</p> <p>Lenders prefer that you don’t use too much of your limit – as a general rule of thumb try to avoid going over 50%. If you go over 100% of your limit, you’ll often face costly fees and may lose any special promotional rates you have on the card.</p>\n',
    },
    'search-history-soft-searches': {
      title: 'Soft searches',
      body: '<p>Lenders cannot see all soft searches and they will never impact your credit score. They include:</p> <ul> <li>simple identity checks, carried out by financial companies after you’ve applied for a (non-credit) product with them,</li> <li>quotation searches, which happen when you check your chances of being accepted before applying (such as in Credit Monitor), and</li> <li>personal checks of your own score or report (such as in Credit Monitor).</li> </ul> <p>To find out more about a particular search, get in touch with the company that carried it out.</p>\n',
    },
    'public-records-judgments': {
      title: 'Judgments',
      body: '<p>County Court Judgments (CCJs), High Court Judgments (HCJs) and Decrees are orders issued by a court when you fail to pay money you owe.</p> <p>These will be visible to potential lenders for six years from being issued, making it much harder to get credit during this time.  The exception to this is if it is paid off within a month of being issued, where it is ‘set aside’ and removed from your credit report.</p> <p>A judgment becomes ‘satisfied’ by you clearing the debt (but will still appear on your credit file).</p>\n',
    },
    'what-is-a-credit-score': {
      title: 'What’s a credit score?',
      body: '<p>Your credit score is a general interpretation of how much of a ‘risk’ lenders might think you are.</p> <p>It’s calculated based on a number of factors including your past behaviour with credit products – things like credit cards, overdrafts, loans and monthly utility accounts. The companies providing these services report your use and payments to a credit reference agency, where it can be seen by future lenders you apply for credit with.  Altogether, this information paints a picture of how reliable you are at repaying money.</p> <p>The higher your score, the more likely you are to be offered cards and loans with cheaper rates and better offers, and the more you’re likely to be able to borrow. Some factors like your income and outgoings don’t affect your score, but are also considered by lenders.</p> <p>The score you see in Credit Monitor is out of 710 and is provided by the credit reference agency TransUnion.</p>\n',
    },
    'accounts-current-accounts': {
      title: 'Current accounts',
      body: '<p>These are the overdraft balances of any current accounts you have. Savings accounts are not reported to the credit reference agency TransUnion.</p> <p>We do not show positive balances, and so if you’re not overdrawn you can expect to see the balance as £0.</p> <p>These overdraft balances are not real time - they’re updated every four to six weeks by the lender, so can be up to ten weeks behind depending on when your report was last updated.</p>\n',
    },
    'accounts-loans': {
      title: 'Loans',
      body: '<p>These are what you’ve got left to pay (the balance) of any loans you have.</p> <p>These balances are not real time - they’re updated every four to six weeks by the bank or lender, so can be up to ten weeks behind depending on when your report was last updated.</p>\n',
    },
    'accounts-other': {
      title: 'Other accounts',
      body: '<p>This covers other types of credit agreements – so long as the lender or bank shares the data with the credit reference agency TransUnion, you’ll find it here (not all do).</p>\n',
    },
    'accounts-telecommunications': {
      title: 'Telecommunications',
      body: "<p>If your mobile provider shares data with the credit reference agency TransUnion, you’ll find it here (not all do).</p> <p>The balance refers to the amount outstanding since your last bill, so if you're up-to-date with payments it should show as £0.</p>\n",
    },
    'personal-linked-addresses': {
      title: 'Linked addresses',
      body: "<p>Linked addresses are any addresses associated with you, as provided by lenders you have accounts with. In most cases these will be previous addresses you’ve moved from/to.</p> <p>Check for any mistakes - if you spot anything, contact the 'source' lender and get it corrected, this will make your credit report more consistent.</p>\n",
    },
    'personal-electoral-roll': {
      title: 'Electoral roll',
      body: '<p>These are your details found on the electoral roll provided by your local council that match up with the current address in the personal details section of the app.</p> <p>If you’ve moved, you’ll need to contact us with your new address for the record to update.</p>\n',
    },
    'accounts-credit-cards': {
      title: 'Credit cards',
      body: '<p>These are the balances of any credit cards you have.</p> <p>These credit card balances are not real time - they’re updated every four to six weeks by the bank or lender, so can be up to ten weeks behind depending on when your report was last updated.</p>\n',
    },
    'accounts-mortgages': {
      title: 'Mortgages',
      body: '<p>These are what you’ve got left to pay (the balance) of any mortgages you have.</p> <p>These balances are not real time - they’re updated every four to six weeks by the bank or lender, so can be up to ten weeks behind depending on when your report was last updated.</p>\n',
    },
    'accounts-utilities': {
      title: 'Utilities',
      body: "<p>If your energy or water provider shares data with the credit reference agency TransUnion, you’ll find it here (not all do).</p> <p>The balance refers to the amount outstanding since your last bill, so if you're up-to-date with payments it should show as £0.</p>\n",
    },
    'summary-loans': {
      title: 'Loans',
      body: '<p>These are what you’ve got left to pay (the balance) of any loans you have, the amount you originally borrowed and how much you’re repaying each month.</p> <p>These balances are not real time - they’re updated every four to six weeks by the bank or lender, so can be up to ten weeks behind depending on when your report was last updated.</p>\n',
    },
    'search-history-hard-searches': {
      title: 'Hard searches',
      body: '<p>When you apply for a credit product a hard search is carried out. These:</p> <ul> <li>are visible to other lenders for two years after taking place,</li> <li>do not reveal whether you were accepted or rejected, or impact your credit score, but</li> <li>may put off potential lenders if you make too many in a short space of time.</li> </ul> <p>To find out more about a particular search, get in touch with the company that carried it out.</p>\n',
    },
    'summary-mortgages': {
      title: 'Mortgages',
      body: '<p>These are what you’ve got left to pay (the balance) of any mortgages you have, the amount you originally borrowed and how much you’re repaying each month.</p> <p>These balances are not real time - they’re updated every four to six weeks by the bank or lender, so can be up to ten weeks behind depending on when your report was last updated.</p>\n',
    },
    'personal-financial-connections': {
      title: 'Financial connections',
      body: '<p>If you’ve shared a credit account with someone in the past, their name will appear as a financial connection.</p> <p>These can be created by joint accounts, joint loans or shared utility bills for example – any credit product with more than one name on it. (This doesn’t include ‘joint credit cards’, as technically they don’t exist – while you may have more than one card, there’s only ever one account-holder.)</p> <p>When you apply for credit, lenders may look at the credit history and other financial info of any connections. If you’re no longer financially linked to someone, raise a dispute and ask for a ‘notice of disassociation’ to get them removed from your file.</p>\n',
    },
  },
};

export const MockCRFullAccountDetails: CRFullGroupAccountDetails = {
  accountLinkId: '2007d11f-21f4-45ef-a276-00a5fe1dcc1d',
  type: {
    classification: CREDIT_REPORT_ACCOUNT_CLASSIFICATIONS.CURRENT_ACCOUNT,
    code: 'SX',
    description: 'Offset Current Account',
  },
  lenderName: 'Test Lender',
  accountNumber: '***************303 1',
  accountId: 50303,
  memberPortId: 292,
  open: true,
  accountHolderDetails: {
    name: 'Mr Xcampbell Cameroon',
    address: '34 X9 9LG',
    dateOfBirth: '1974-04-04',
    accountHolderId: 50314,
  },
  balance: 500,
  balanceHistory: {
    "items": [
      {
        date: '2020-07-01',
        value: 1000,
      },
      {
        date: '2020-06-01',
        value: 1000,
      },
      {
        date: '2020-05-01',
        value: 1000,
      },
      {
        date: '2020-04-01',
        value: 1000,
      },
      {
        date: '2020-03-01',
        value: 1000,
      },
    ],
  },
  limitHistory: {
    "items": [
      {
        date: '2020-07-01',
        value: 1000,
      },
      {
        date: '2020-06-01',
        value: 1000,
      },
      {
        date: '2020-05-01',
        value: 1000,
      },
      {
        date: '2020-04-01',
        value: 1000,
      },
      {
        date: '2020-03-01',
        value: 1000,
      },
    ],
  },
  currencyCode: 'GBP',
  updatedDate: '2020-07-07T23:00:00Z',
  accountStartDate: '2020-03-08T00:00:00Z',
  promotionalRate: false,
  openingBalance: 100,
  paymentStartDate: '2006-09-11T23:00:00Z',
  repaymentFrequency: 'Monthly',
  status: 'UP_TO_DATE',
  additionalStatus: {
    value: 'noAction',
    shortDescription: 'Your payments are up-to-date',
    description: 'Your payments are up-to-date',
  },
  minimumPayment: false,
  statusSubjectiveLevel: 'GOOD',
  statusHistory: {
    "items": [
      {
        date: '2020-07-01',
        value: {
          paymentStatus: '0',
          paymentStatusDescription: 'Account payments up to date',
          accountStatus: 'OK',
          accountDescription: 'Ok',
        },
      },
      {
        date: '2020-06-01',
        value: {
          paymentStatus: '1',
          paymentStatusDescription: 'Payment was up to one month late',
          accountStatus: 'AA',
          accountDescription: 'Early arrears',
        },
      },
      {
        date: '2020-05-01',
        value: {
          paymentStatus: '0',
          paymentStatusDescription: 'Account payments up to date',
          accountStatus: 'OK',
          accountDescription: 'Ok',
        },
      },
      {
        date: '2020-04-01',
        value: {
          paymentStatus: '0',
          paymentStatusDescription: 'Account payments up to date',
          accountStatus: 'OK',
          accountDescription: 'Ok',
        },
      },
      {
        date: '2020-03-01',
        value: {
          paymentStatus: '0',
          paymentStatusDescription: 'Account payments up to date',
          accountStatus: 'OK',
          accountDescription: 'Ok',
        },
      },
    ],
  },
  history: {
    "items": [
      {
        date: '2020-07-01',
        value: {
          limit: 1000,
          balance: 1000,
          paymentStatus: '0',
          paymentStatusDescription: 'Account payments up to date',
          accountStatus: 'OK',
          accountDescription: 'Ok',
        },
      },
      {
        date: '2020-06-01',
        value: {
          limit: 1000,
          balance: 1000,
          paymentStatus: '1',
          paymentStatusDescription: 'Payment was up to one month late',
          accountStatus: 'AA',
          accountDescription: 'Early arrears',
        },
      },
      {
        date: '2020-05-01',
        value: {
          limit: 1000,
          balance: 1000,
          paymentStatus: '0',
          paymentStatusDescription: 'Account payments up to date',
          accountStatus: 'OK',
          accountDescription: 'Ok',
        },
      },
      {
        date: '2020-04-01',
        value: {
          limit: 1000,
          balance: 1000,
          paymentStatus: '0',
          paymentStatusDescription: 'Account payments up to date',
          accountStatus: 'OK',
          accountDescription: 'Ok',
        },
      },
      {
        date: '2020-03-01',
        value: {
          limit: 1000,
          balance: 1000,
          paymentStatus: '0',
          paymentStatusDescription: 'Account payments up to date',
          accountStatus: 'OK',
          accountDescription: 'Ok',
        },
      },
    ],
  },
  limit: 1000,
};

export const MockTestKbaAddress: Address = {
  postcode: 'X9 1WS',
  countryCode: 'GB',
  postTown: 'TEST TOWN',
  dependentLocality: 'test',
  thoroughfare: 'Monopoly Street',
  county: 'FAKE COUNTY',
  buildingNumber: '5',
  label: '5, Monopoly Street, TEST TOWN, FAKE COUNTY, X9 1WS',
};

export const MockTestOtpAddress: Address = {
  postcode: 'X9 1WS',
  countryCode: 'GB',
  postTown: 'TEST TOWN',
  dependentLocality: 'test',
  thoroughfare: 'NoKBA Street',
  county: 'FAKE COUNTY',
  buildingNumber: '4',
  label: '4, NoKBA Street, TEST TOWN, FAKE COUNTY, X9 1WS',
};

export const MockNegativeIdAddress: Address = {
  postcode: 'X9 1WS',
  countryCode: 'GB',
  postTown: 'TEST TOWN',
  dependentLocality: 'test',
  thoroughfare: 'NegRolland Street',
  county: 'FAKE COUNTY',
  buildingNumber: '2',
  label: '2, NegRolland Street, TEST TOWN, FAKE COUNTY, X9 1WS',
};

export const MockThinFileAddress: Address = {
  postcode: 'X9 1WS',
  countryCode: 'GB',
  postTown: 'TEST TOWN',
  dependentLocality: 'test',
  thoroughfare: 'Headroom Street',
  county: 'FAKE COUNTY',
  buildingNumber: '3',
  label: '3, Headroom Street, TEST TOWN, FAKE COUNTY, X9 1WS',
};
